/**
 * @typedef {Object} CurrentAppUserDto
 * @property {number} userId
 * @property {string|null} entraObjectId
 * @property {string} email
 * @property {string|null} displayName
 * @property {boolean} isActive
 * @property {string} createdAtUtc
 * @property {string|null} updatedAtUtc
 * @property {number|null} supplierId
 * @property {number|null} roleId
 * @property {string|null} roleName
 * @property {string[]} permissions
 * @property {string[]} executablePermissions
 */

/**
 * @typedef {Object} ApplicationResourceDto
 * @property {number} resourceId
 * @property {string} code
 * @property {string} name
 * @property {number|null} parentId
 * @property {number} displayOrder
 * @property {boolean} isActive
 * @property {boolean} isAssignable
 * @property {string} createdAtUtc
 * @property {string|null} updatedAtUtc
 * @property {boolean} isCompanyScoped
 */

/**
 * @typedef {Object} UserPermissionSetDto
 * @property {number} userId
 * @property {number[]} resourceIds
 * @property {string[]} resourceCodes
 * @property {string[]} effectivePermissions
 * @property {EffectivePermissionDetailDto[]} effectivePermissionDetails
 * @property {PermissionAssignmentDto[]} assignedPermissions
 */

/**
 * @typedef {Object} EffectivePermissionDetailDto
 * @property {number} resourceId
 * @property {string} code
 * @property {boolean} isDirect
 * @property {boolean} isInherited
 * @property {number|null} inheritedRoleId
 * @property {boolean} canExecute
 * @property {boolean|null} directCanExecute
 * @property {boolean|null} inheritedCanExecute
 */

/**
 * @typedef {Object} AppRoleDto
 * @property {number} roleId
 * @property {string|null} roleName
 * @property {boolean} isActive
 * @property {string} createdAtUtc
 * @property {string|null} updatedAtUtc
 */

/**
 * @typedef {Object} RolePermissionSetDto
 * @property {number} roleId
 * @property {number[]} resourceIds
 * @property {string[]} resourceCodes
 * @property {string[]} effectivePermissions
 * @property {PermissionAssignmentDto[]} assignedPermissions
 * @property {string[]} executablePermissions
 */

/**
 * @typedef {Object} PermissionGrantRequest
 * @property {number} resourceId
 * @property {boolean} canExecute
 */

/**
 * @template T
 * @typedef {Object} SecurityPagedResult
 * @property {T[]} items
 * @property {number} page
 * @property {number} pageSize
 * @property {number} totalCount
 * @property {number} totalPages
 */

/**
 * @typedef {Object} CompanyAccessProfileDto
 * @property {number} companyAccessProfileId
 * @property {string} name
 * @property {string|null} description
 * @property {boolean} isActive
 * @property {string} createdAtUtc
 * @property {string|null} updatedAtUtc
 * @property {number} companyCount
 * @property {number} resourceCount
 * @property {number} roleCount
 * @property {number} userCount
 */

/** @typedef {{name: string, description?: string|null}} SaveCompanyAccessProfileRequest */
/** @typedef {{isActive: boolean}} SetCompanyAccessProfileStatusRequest */
/** @typedef {{companyId: number, companyName: string, companyDb: string, isActive: boolean}} CompanyAccessProfileCompanyDto */
/** @typedef {CompanyAccessProfileCompanyDto & {isSelected: boolean}} CompanyAccessCompanyOptionDto */
/** @typedef {{companyIds: number[]}} ReplaceCompanyAccessProfileCompaniesRequest */
/** @typedef {{resourceId: number, code: string, name: string, canExecute: boolean, isActive: boolean}} CompanyAccessProfileResourceDto */
/** @typedef {{resourceId: number, canExecute: boolean}} CompanyAccessResourceGrantRequest */
/** @typedef {{resources: CompanyAccessResourceGrantRequest[]}} ReplaceCompanyAccessProfileResourcesRequest */
/** @typedef {{roleId: number, roleName: string|null, isActive: boolean}} CompanyAccessProfileRoleDto */
/** @typedef {CompanyAccessProfileRoleDto & {isSelected: boolean}} CompanyAccessRoleOptionDto */
/** @typedef {{roleIds: number[]}} ReplaceCompanyAccessProfileRolesRequest */
/** @typedef {{userId: number, email: string, displayName: string|null, isActive: boolean, roleId: number|null, roleName: string|null}} CompanyAccessProfileUserDto */
/** @typedef {CompanyAccessProfileUserDto & {isSelected: boolean}} CompanyAccessUserOptionDto */
/** @typedef {{userIds: number[]}} ReplaceCompanyAccessProfileUsersRequest */

/**
 * @typedef {Object} EffectiveCompanyAccessSourceDto
 * @property {number} companyAccessProfileId
 * @property {string} profileName
 * @property {boolean} isDirectUserProfile
 * @property {boolean} isInheritedFromRole
 * @property {number|null} roleId
 * @property {boolean} canExecute
 */

/**
 * @typedef {Object} EffectiveCompanyAccessDto
 * @property {number} userId
 * @property {string} email
 * @property {string|null} displayName
 * @property {number} companyId
 * @property {string} companyName
 * @property {number} resourceId
 * @property {string} resourceCode
 * @property {'None'|'View'|'Execute'} globalCapability
 * @property {'View'|'Execute'} companyCapability
 * @property {'None'|'View'|'Execute'} finalCapability
 * @property {EffectiveCompanyAccessSourceDto[]} sources
 */
/**
 * @typedef {Object} PermissionAssignmentDto
 * @property {number} resourceId
 * @property {string} code
 * @property {boolean} canExecute
 */

const value = (source, camel, pascal, fallback = null) => source?.[camel] ?? source?.[pascal] ?? fallback;
const assignments = source => value(source, 'assignedPermissions', 'AssignedPermissions', []).map(grant => ({
    resourceId: value(grant, 'resourceId', 'ResourceId'),
    code: value(grant, 'code', 'Code', ''),
    canExecute: value(grant, 'canExecute', 'CanExecute', false)
}));

export const normalizeApplicationResource = resource => ({
    resourceId: value(resource, 'resourceId', 'ResourceId'),
    code: value(resource, 'code', 'Code', ''),
    name: value(resource, 'name', 'Name', ''),
    parentId: value(resource, 'parentId', 'ParentId'),
    displayOrder: value(resource, 'displayOrder', 'DisplayOrder', 0),
    isActive: value(resource, 'isActive', 'IsActive', false),
    isAssignable: value(resource, 'isAssignable', 'IsAssignable', false),
    createdAtUtc: value(resource, 'createdAtUtc', 'CreatedAtUtc'),
    updatedAtUtc: value(resource, 'updatedAtUtc', 'UpdatedAtUtc'),
    isCompanyScoped: value(resource, 'isCompanyScoped', 'IsCompanyScoped', false)
});

/**
 * Runtime company access is computed by the backend from Company Access Profiles.
 * @typedef {Object} RuntimeCompanyAccessGrantDto
 * @property {number} companyId
 * @property {string} companyName
 * @property {string} companyDb
 * @property {string} resourceCode
 * @property {boolean} canExecute
 */
/** @typedef {{userId: number, access: RuntimeCompanyAccessGrantDto[]}} RuntimeCompanyAccessDto */
export const normalizeRuntimeCompanyAccess = response => ({
    userId: value(response, 'userId', 'UserId'),
    access: value(response, 'access', 'Access', []).map(grant => ({
        companyId: value(grant, 'companyId', 'CompanyId'),
        companyName: value(grant, 'companyName', 'CompanyName', ''),
        companyDb: value(grant, 'companyDb', 'CompanyDb', ''),
        resourceCode: value(grant, 'resourceCode', 'ResourceCode', ''),
        canExecute: value(grant, 'canExecute', 'CanExecute', false)
    }))
});

export const normalizeUserPermissionSet = permissionSet => ({
    assignedPermissions: assignments(permissionSet),
    userId: value(permissionSet, 'userId', 'UserId'),
    resourceIds: value(permissionSet, 'resourceIds', 'ResourceIds', []),
    resourceCodes: value(permissionSet, 'resourceCodes', 'ResourceCodes', []),
    effectivePermissions: value(permissionSet, 'effectivePermissions', 'EffectivePermissions', []),
    effectivePermissionDetails: value(
        permissionSet,
        'effectivePermissionDetails',
        'EffectivePermissionDetails',
        []
    ).map(detail => ({
        resourceId: value(detail, 'resourceId', 'ResourceId'),
        code: value(detail, 'code', 'Code', ''),
        isDirect: value(detail, 'isDirect', 'IsDirect', false),
        isInherited: value(detail, 'isInherited', 'IsInherited', false),
        inheritedRoleId: value(detail, 'inheritedRoleId', 'InheritedRoleId'),
        canExecute: value(detail, 'canExecute', 'CanExecute', false),
        directCanExecute: value(detail, 'directCanExecute', 'DirectCanExecute'),
        inheritedCanExecute: value(detail, 'inheritedCanExecute', 'InheritedCanExecute')
    }))
});

export const normalizeAppRole = role => ({
    roleId: value(role, 'roleId', 'RoleId'),
    roleName: value(role, 'roleName', 'RoleName'),
    isActive: value(role, 'isActive', 'IsActive', false),
    createdAtUtc: value(role, 'createdAtUtc', 'CreatedAtUtc'),
    updatedAtUtc: value(role, 'updatedAtUtc', 'UpdatedAtUtc')
});

export const normalizeRolePermissionSet = permissionSet => ({
    assignedPermissions: assignments(permissionSet),
    executablePermissions: value(permissionSet, 'executablePermissions', 'ExecutablePermissions', []),
    roleId: value(permissionSet, 'roleId', 'RoleId'),
    resourceIds: value(permissionSet, 'resourceIds', 'ResourceIds', []),
    resourceCodes: value(permissionSet, 'resourceCodes', 'ResourceCodes', []),
    effectivePermissions: value(permissionSet, 'effectivePermissions', 'EffectivePermissions', [])
});

const resourceSort = (left, right) => (
    left.displayOrder - right.displayOrder
    || left.name.localeCompare(right.name)
    || left.resourceId - right.resourceId
);

export const buildApplicationResourceTree = resources => {
    const nodes = (resources || []).map(resource => ({ ...normalizeApplicationResource(resource), children: [] }));
    const byId = new Map(nodes.map(node => [node.resourceId, node]));
    const roots = [];

    nodes.forEach(node => {
        const parent = node.parentId == null ? null : byId.get(node.parentId);
        if (parent) parent.children.push(node);
        else roots.push(node);
    });

    const sortChildren = node => {
        node.children.sort(resourceSort);
        node.children.forEach(sortChildren);
    };
    roots.sort(resourceSort);
    roots.forEach(sortChildren);
    return roots;
};
